/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService, ApiResponse } from '../core';

/**
 * Organization interface
 */
export interface Organization {
  _id: string;
  id?: string;
  name: string;
  description?: string;
  logoUrl?: string;
  teamsCount?: number;
  membersCount?: number;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/**
 * Organization Overview interface
 */
export interface OrganizationOverview {
  id: string;
  name: string;
  description: string;
  teamsCount: number;
  membersCount: number;
  recentActivity: Array<{
    id: string;
    type: string;
    description: string;
    timestamp: string;
  }>;
  statistics: {
    totalContent: number;
    activeTeams: number;
    storageUsed: number;
  };
}

/**
 * Organization Member interface
 */
export interface OrganizationMember {
  id: string;
  user_id: string;
  organization_id: string;
  role_id: string;
  role_name?: string;
  user?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  // API v2 also returns these directly
  name?: string;
  email?: string;
  status?: string;
  joined_at?: string;
  invited_by?: string;
}

/**
 * Organization Team interface
 */
export interface OrganizationTeam {
  _id: string;
  id?: string;
  name: string;
  description?: string;
  organizationId: string;
  subscriptionId?: string;
  membersCount?: number;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/**
 * Organization Subscription interface
 */
export interface OrganizationSubscription {
  _id: string;
  id?: string;
  plan_id: string;
  plan_name?: string;
  status: string;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  limits?: {
    max_teams?: number;
    max_members?: number;
    max_storage_gb?: number;
    max_content_items?: number;
  };
  usage?: {
    teams_count?: number;
    members_count?: number;
    storage_used_gb?: number;
    content_items_count?: number;
  };
}

/**
 * Organization Billing Details interface
 */
export interface OrganizationBillingDetails {
  name?: string;
  email?: string;
  phone?: string;
  taxId?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
}

/**
 * Create Organization Request
 */
export interface CreateOrganizationRequest {
  name: string;
  description?: string;
}

/**
 * Update Organization Request
 */
export interface UpdateOrganizationRequest {
  name?: string;
  description?: string;
  logoUrl?: string;
}

/**
 * Organization Service
 * 
 * Handles all organization-related operations including:
 * - Organization CRUD operations
 * - Organization member management
 * - Organization team management
 * - Organization subscription management
 * - Organization billing and settings
 * 
 * Extracted from the monolithic API service.
 */
export class OrganizationService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  // ===== ORGANIZATION CRUD OPERATIONS =====

  /**
   * Get organization by ID
   */
  async getOrganizationById(organizationId: string): Promise<Organization | null> {
    try {
      console.log(`Fetching organization: ${organizationId}`);
      
      const response = await this.getCached<any>(
        `/organizations/${organizationId}`,
        undefined,
        { timeout: 10000 }
      );
      
      return this.normalizeOrganization(response);
    } catch (error) {
      console.error(`Error fetching organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.getOrganizationById(${organizationId})`);
    }
  }

  /**
   * Get organization with API response wrapper
   */
  async getOrganization(organizationId: string): Promise<ApiResponse<Organization>> {
    try {
      console.log(`Fetching organization with metadata: ${organizationId}`);
      
      const response = await this.requestWithMetadata<Organization>(
        'GET',
        `/organizations/${organizationId}`,
        undefined,
        { timeout: 10000 }
      );
      
      return response;
    } catch (error) {
      console.error(`Error fetching organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.getOrganization(${organizationId})`);
    }
  }

  /**
   * Get organization details
   */
  async getOrganizationDetails(organizationId: string): Promise<Organization> {
    try {
      console.log(`Fetching organization details: ${organizationId}`);
      
      const response = await this.get<any>(`/organizations/${organizationId}`);
      
      // The API response has the organization data nested under 'organization' key
      const orgData = response.organization || response;
      
      return {
        _id: orgData.id || orgData._id || '',
        id: orgData.id || orgData._id || '',
        name: orgData.name || '',
        description: orgData.description || '',
        logoUrl: orgData.logo_url || orgData.logoUrl || '',
        teamsCount: orgData.teams_count || orgData.teamCount || orgData.teamsCount || 0,
        membersCount: orgData.members_count || orgData.memberCount || orgData.membersCount || 0,
        // Map API v2 field names (createdOn/updatedOn) to our interface (createdAt/updatedAt)
        createdAt: orgData.createdOn || orgData.created_at || orgData.createdAt,
        updatedAt: orgData.updatedOn || orgData.updated_at || orgData.updatedAt
      };
    } catch (error) {
      console.error(`Error fetching organization details ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.getOrganizationDetails(${organizationId})`);
    }
  }

  /**
   * Get organization overview with statistics
   */
  async getOrganizationOverview(organizationId: string): Promise<OrganizationOverview> {
    try {
      console.log(`Fetching organization overview: ${organizationId}`);
      
      const response = await this.getCached<any>(
        `/organizations/${organizationId}/overview`
      );
      
      return {
        id: response.id || response._id,
        name: response.name,
        description: response.description || '',
        teamsCount: response.teamsCount || response.teams_count || 0,
        membersCount: response.membersCount || response.members_count || 0,
        recentActivity: response.recentActivity || response.recent_activity || [],
        statistics: {
          totalContent: response.statistics?.totalContent || response.statistics?.total_content || 0,
          activeTeams: response.statistics?.activeTeams || response.statistics?.active_teams || 0,
          storageUsed: response.statistics?.storageUsed || response.statistics?.storage_used || 0
        }
      };
    } catch (error) {
      console.error(`Error fetching organization overview ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.getOrganizationOverview(${organizationId})`);
    }
  }

  /**
   * Create a new organization
   */
  async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    try {
      console.log('Creating organization:', data.name);
      
      const response = await this.post<any>('/organizations', data);
      
      // Invalidate user cache since a new organization was created
      this.invalidateCache('user');
      
      // The API response may have the organization data nested
      const orgData = response.organization || response;
      
      return {
        id: orgData.id || orgData._id,
        _id: orgData.id || orgData._id,
        name: orgData.name,
        description: orgData.description,
        logoUrl: orgData.logo_url || orgData.logoUrl,
        teamsCount: orgData.teams_count || orgData.teamCount || orgData.teamsCount || 0,
        membersCount: orgData.members_count || orgData.memberCount || orgData.membersCount || 1,
        // Map API v2 field names (createdOn/updatedOn) to our interface (createdAt/updatedAt)
        createdAt: orgData.createdOn || orgData.created_at || orgData.createdAt || new Date(),
        updatedAt: orgData.updatedOn || orgData.updated_at || orgData.updatedAt || new Date()
      };
    } catch (error) {
      console.error('Error creating organization:', error);
      throw this.errorHandler.handleError(error, 'OrganizationService.createOrganization');
    }
  }

  /**
   * Update organization details
   */
  async updateOrganization(organizationId: string, data: UpdateOrganizationRequest): Promise<Organization> {
    try {
      console.log(`Updating organization ${organizationId}:`, data);
      
      const response = await this.put<any>(`/organizations/${organizationId}`, data);
      
      // Invalidate caches for this organization
      this.invalidateCache('organization');
      this.invalidateCache('user');
      
      // The API response has the organization data nested under 'organization' key
      const orgData = response.organization || response;
      
      return {
        _id: orgData.id || orgData._id || '',
        id: orgData.id || orgData._id || '',
        name: orgData.name || '',
        description: orgData.description || '',
        logoUrl: orgData.logo_url || orgData.logoUrl || '',
        teamsCount: orgData.teams_count || orgData.teamCount || orgData.teamsCount || 0,
        membersCount: orgData.members_count || orgData.memberCount || orgData.membersCount || 0,
        // Map API v2 field names (createdOn/updatedOn) to our interface (createdAt/updatedAt)
        createdAt: orgData.createdOn || orgData.created_at || orgData.createdAt,
        updatedAt: orgData.updatedOn || orgData.updated_at || orgData.updatedAt
      };
    } catch (error) {
      console.error(`Error updating organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.updateOrganization(${organizationId})`);
    }
  }

  /**
   * Delete an organization
   */
  async deleteOrganization(organizationId: string): Promise<void> {
    try {
      console.log(`Deleting organization: ${organizationId}`);

      await this.delete(`/organizations/${organizationId}`);

      // Invalidate caches for this organization
      this.invalidateCache('organization');
      this.invalidateCache('user');
    } catch (error) {
      console.error(`Error deleting organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.deleteOrganization(${organizationId})`);
    }
  }

  /**
   * Leave an organization
   */
  async leaveOrganization(organizationId: string, reason?: string): Promise<void> {
    try {
      console.log(`Leaving organization: ${organizationId}`);

      await this.post(`/organizations/${organizationId}/leave`, { reason });

      // Invalidate caches for this organization
      this.invalidateCache('organization');
      this.invalidateCache('user');
    } catch (error) {
      console.error(`Error leaving organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.leaveOrganization(${organizationId})`);
    }
  }

  // ===== ORGANIZATION MEMBER OPERATIONS =====

  /**
   * List organization members
   */
  async listOrganizationMembers(organizationId: string): Promise<OrganizationMember[]> {
    try {
      console.log(`Fetching members for organization ${organizationId}`);
      
      const response = await this.getCached<any>(
        `/organizations/${organizationId}/members`
      );
      
      // Handle both array and wrapped response formats
      let members: any[] = [];
      if (Array.isArray(response)) {
        members = response;
      } else if (response?.members && Array.isArray(response.members)) {
        members = response.members;
      } else if (response?.data && Array.isArray(response.data)) {
        members = response.data;
      }
      
      return members.map(member => ({
        id: member.id || member._id,
        user_id: member.user_id || member.userId,
        organization_id: member.organization_id || member.organizationId || organizationId,
        role_id: member.role_id || member.roleId,
        role_name: member.role_name || member.roleName,
        // API v2 returns name and email directly, not in nested user object
        user: member.user || (member.name || member.email ? {
          id: member.userId || member.user_id,
          name: member.name,
          email: member.email,
          avatar: member.avatar
        } : undefined),
        // Also include name and email at the root level for compatibility
        name: member.name,
        email: member.email,
        status: member.status,
        joined_at: member.joined_at || member.joinedAt,
        invited_by: member.invited_by || member.invitedBy
      }));
    } catch (error) {
      console.error(`Error fetching members for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.listOrganizationMembers(${organizationId})`);
    }
  }

  // ===== ORGANIZATION TEAM OPERATIONS =====

  /**
   * List organization teams
   */
  async listOrganizationTeams(
    organizationId: string,
    options?: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    }
  ): Promise<OrganizationTeam[]> {
    try {
      const { page = 1, limit = 10, search, status } = options || {};

      console.log(`Fetching teams for organization ${organizationId}`, { page, limit, search, status });

      // Build query parameters
      const params: Record<string, string | number> = { page, limit };
      if (search) params.search = search;
      if (status) params.status = status;

      const response = await this.get<any>(
        `/organizations/${organizationId}/teams`,
        { params }
      );

      // Handle both array and wrapped response formats
      let teams: any[] = [];
      if (Array.isArray(response)) {
        teams = response;
      } else if (response?.teams && Array.isArray(response.teams)) {
        teams = response.teams;
      } else if (response?.data && Array.isArray(response.data)) {
        teams = response.data;
      }

      return teams.map(team => ({
        _id: team.id || team._id,
        id: team.id || team._id,
        name: team.name,
        description: team.description,
        organizationId: team.organization_id || team.organizationId || organizationId,
        subscriptionId: team.subscription_id || team.subscriptionId,
        membersCount: team.members_count || team.membersCount,
        storageUsedBytes: team.storage_used_bytes || team.storageUsedBytes || 0,
        createdAt: team.created_at || team.createdAt,
        updatedAt: team.updated_at || team.updatedAt
      }));
    } catch (error) {
      console.error(`Error fetching teams for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.listOrganizationTeams(${organizationId})`);
    }
  }

  /**
   * List organization teams with metadata (for pagination)
   */
  async listOrganizationTeamsWithMetadata(
    organizationId: string,
    options?: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    }
  ): Promise<ApiResponse<OrganizationTeam[]>> {
    try {
      const { page = 1, limit = 10, search, status } = options || {};

      console.log(`Fetching teams with metadata for organization ${organizationId}`, { page, limit, search, status });

      // Build query parameters
      const params: Record<string, string | number> = { page, limit };
      if (search) params.search = search;
      if (status) params.status = status;

      const response = await this.requestWithMetadata<OrganizationTeam[]>(
        'GET',
        `/organizations/${organizationId}/teams`,
        undefined,
        { params }
      );

      return response;
    } catch (error) {
      console.error(`Error fetching teams with metadata for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.listOrganizationTeamsWithMetadata(${organizationId})`);
    }
  }

  // ===== ORGANIZATION SUBSCRIPTION OPERATIONS =====

  /**
   * Get organization subscriptions (paginated response)
   */
  async getOrganizationSubscriptions(organizationId: string): Promise<OrganizationSubscription[]> {
    try {
      console.log(`Fetching subscriptions for organization ${organizationId}`);

      const response = await this.getCached<any>(
        `/organizations/${organizationId}/subscriptions`
      );

      // Handle paginated response format: { data: [...], pagination: {...} }
      const subscriptions = response?.data || response || [];

      return subscriptions.map((sub: any) => ({
        ...sub,
        _id: sub.id || sub._id,
        id: sub.id || sub._id
      }));
    } catch (error) {
      console.warn(`Failed to fetch subscriptions for organization ${organizationId}:`, error);

      // For non-critical errors (404, 500), return empty array
      // This allows the app to continue functioning
      const status = this.errorHandler.getErrorStatus(error);
      if (status === 404 || status === 500 || status === 503) {
        return [];
      }

      // For auth errors, re-throw so the app can handle authentication
      if (status === 401 || status === 403) {
        throw this.errorHandler.handleError(error, `OrganizationService.getOrganizationSubscriptions(${organizationId})`);
      }

      // Default to empty array for other errors
      return [];
    }
  }

  // ===== ORGANIZATION BILLING OPERATIONS =====

  /**
   * Update organization billing details
   */
  async updateOrganizationBillingDetails(
    organizationId: string,
    billingDetails: OrganizationBillingDetails
  ): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`Updating billing details for organization ${organizationId}`);
      
      await this.put(`/organizations/${organizationId}/billing`, billingDetails);
      
      // Invalidate cache
      this.invalidateCache('organization');
      
      return {
        success: true,
        message: 'Billing details updated successfully'
      };
    } catch (error) {
      console.error(`Error updating billing details for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.updateOrganizationBillingDetails(${organizationId})`);
    }
  }

  /**
   * Update organization member role
   */
  async updateOrganizationMemberRole(organizationId: string, userId: string, roleId: string): Promise<void> {
    try {
      console.log(`Updating role for member ${userId} in organization ${organizationId} to role ${roleId}`);

      const requestBody = { roleId: roleId };
      console.log('Request body:', JSON.stringify(requestBody));

      const response = await this.put(`/organizations/${organizationId}/members/${userId}`, requestBody);
      console.log('Update member role response:', response);

      // Invalidate cache
      this.invalidateCache('organization');
    } catch (error) {
      console.error(`Error updating member role in organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.updateOrganizationMemberRole(${organizationId})`);
    }
  }

  /**
   * Remove organization member (admin action)
   */
  async removeOrganizationMember(organizationId: string, userId: string): Promise<void> {
    try {
      console.log(`Removing member ${userId} from organization ${organizationId}`);

      await this.delete(`/organizations/${organizationId}/members/${userId}`);

      // Invalidate cache
      this.invalidateCache('organization');
    } catch (error) {
      console.error(`Error removing member from organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `OrganizationService.removeOrganizationMember(${organizationId})`);
    }
  }

  // ===== HELPER METHODS =====

  /**
   * Normalize organization data from API response
   */
  private normalizeOrganization(response: any): Organization {
    // Handle API v2 response structure where data might be nested
    const orgData = response.organization || response;
    
    return {
      _id: orgData.id || orgData._id || '',
      id: orgData.id || orgData._id || '',
      name: orgData.name || '',
      description: orgData.description || '',
      logoUrl: orgData.logo_url || orgData.logoUrl || '',
      teamsCount: orgData.teams_count || orgData.teamCount || orgData.teamsCount || 0,
      membersCount: orgData.members_count || orgData.memberCount || orgData.membersCount || 0,
      // Map API v2 field names (createdOn/updatedOn) to our interface (createdAt/updatedAt)
      createdAt: orgData.createdOn || orgData.created_at || orgData.createdAt,
      updatedAt: orgData.updatedOn || orgData.updated_at || orgData.updatedAt
    };
  }
}

// Export singleton instance
export const organizationService = new OrganizationService();